import React, { useEffect, useRef, useState } from "react";
import "./WhoWeAre.css";

function WhoWeAre() {
  const [isFacilitiesExpanded, setIsFacilitiesExpanded] = useState(false);
  const [isCeoVideoOpen, setIsCeoVideoOpen] = useState(false);
  const videoTriggerRef = useRef(null);
  const videoCloseButtonRef = useRef(null);
  const videoDialogRef = useRef(null);

  useEffect(() => {
    if (!isCeoVideoOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    videoCloseButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsCeoVideoOpen(false);
        return;
      }

      if (event.key !== "Tab") return;

      const focusableElements = videoDialogRef.current?.querySelectorAll(
        'button, video[controls], a[href]'
      );
      if (!focusableElements?.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      videoTriggerRef.current?.focus();
    };
  }, [isCeoVideoOpen]);

  return (
    <main className="who-page">

      {/* =====================================================
          HERO
      ===================================================== */}
      <section className="who-hero">
        <img
          className="who-hero-image"
          src="/images/photos/Safety Briefing2.webp"
          alt="Field personnel receiving a safety briefing beside the water"
        />
        <div className="who-hero-overlay"></div>
        <div className="who-container who-hero-content">
          <span className="who-label">WHO WE ARE</span>
          <h1>
            Building Energy.
            <br />
            <span>Enabling Progress.</span>
          </h1>
          <p>
            Discover the story, values and commitment behind Strides Energy
            &amp; Maritime Limited.
          </p>
        </div>
      </section>

      {/* =====================================================
          OUR STORY
      ===================================================== */}
      <section className="who-story section-white">

        <div className="who-container">

          <div className="section-heading">

            <span className="who-label">
              01 — OUR STORY
            </span>

            <h2>
              A journey built on
              <br />
              <span>experience.</span>
            </h2>

          </div>


          <div className="story-grid">

            <div className="story-image">

              <img
                src="/images/dredging.webp"
                alt="Strides Energy maritime operations"
              />

            </div>


            <div className="story-content">

              <h3>
                Company Overview
              </h3>

              <p>
                <strong>Strides Energy & Maritime Limited (SEML)</strong> is a
                <strong> Nigerian-based offshore, swamp oil &amp; gas
                construction and maritime services company</strong>, delivering
                <strong> Engineering, Procurement, Construction, Installation
                and Commissioning (EPCIC)</strong> solutions across the
                <strong> African energy sector</strong>.
              </p>

              <p>
                Established in <strong>1992</strong> and transitioning into
                <strong> oil &amp; gas construction and maritime support
                services in 2000</strong>, SEML has developed a strong
                reputation for
                <strong> safe, efficient, reliable and high-quality project
                execution</strong>.
              </p>

              <p>
                Our capabilities combine
                <strong> engineering expertise, marine assets, technical
                competence, project management and experienced personnel</strong>
                to deliver complex projects across the
                <strong> offshore, swamp, subsea and energy infrastructure
                sectors</strong>.
              </p>

              <button
                type="button"
                className="who-cta-button"
                aria-haspopup="dialog"
                onClick={(event) => {
                  videoTriggerRef.current = event.currentTarget;
                  setIsCeoVideoOpen(true);
                }}
              >
                A few words from our CEO <span aria-hidden="true">→</span>
              </button>

            </div>

          </div>


          <div className="story-details">
            <section className="story-detail">
              <h3>Core Services</h3>
              <ul>
                <li>Offshore & swamp oil and gas construction</li>
                <li>Engineering, Procurement, Construction & Installation (EPCI/EPCIC)</li>
                <li>Pipeline construction and installation</li>
                <li>Subsea inspection, maintenance & repair (IMR)</li>
                <li>Diving services and ROV operations</li>
                <li>Petroleum tank farm construction & rehabilitation</li>
                <li>Subsea umbilicals & risers fabrication and installation</li>
                <li>Offshore vessel supply & marine operations</li>
                <li>Hook-up & commissioning services</li>
                <li>Marine construction & offshore support services</li>
              </ul>
            </section>

            <section className="story-detail">
              <h3>Technical Capabilities</h3>
              <ul>
                <li>Offshore construction vessels</li>
                <li>Marine & offshore construction</li>
                <li>Deepwater inspection services</li>
                <li>Subsea engineering & inspection</li>
                <li>Diving & ROV technology</li>
                <li>Integrated project execution</li>
                <li>Engineering & technical services</li>
                <li>Project management</li>
                <li>Installation & commissioning</li>
                <li>Marine logistics & support</li>
              </ul>
            </section>

            <section className="story-detail">
              <h3>Industry Experience</h3>
              <p>
                SEML has experience supporting major players within the
                <strong> African oil &amp; gas and energy industry</strong>, with
                exposure to projects involving leading international and
                indigenous energy companies, including:
              </p>
              <p className="story-partners">
                <strong>Shell | Chevron | TotalEnergies | ExxonMobil | Renaissance</strong>
              </p>
              <p>
                Our experience spans
                <strong> offshore construction, marine operations, subsea
                services, pipeline infrastructure, facility construction and
                project support</strong>.
              </p>
            </section>

            <section className="story-detail">
              <h3>Certifications & Compliance</h3>
              <p>
                Our commitment to
                <strong> quality, environmental responsibility, occupational
                health, safety and regulatory compliance</strong> is supported
                by relevant industry certifications and approvals, including:
              </p>
              <ul>
                <li>ISO 9001:2015 - Quality Management</li>
                <li>ISO 14001 - Environmental Management</li>
                <li>ISO 45001 - Occupational Health & Safety</li>
                <li>DPR/NUPRC Regulatory Certification</li>
                <li>NIMASA Certification</li>
                <li>ABS Recognition</li>
                <li>Federal Ministry Licences & Approvals</li>
              </ul>
            </section>

            <section className="story-detail story-strengths-detail">
              <h3>Company Strength</h3>
              <div className="story-strength-grid">
                <article>
                  <h4>Local Expertise</h4>
                  <p>Deep understanding of the Nigerian and African oil &amp; gas operating environment, supported by experienced local personnel.</p>
                </article>
                <article>
                  <h4>Offshore Delivery</h4>
                  <p>Proven capability in offshore, swamp, marine and subsea project execution.</p>
                </article>
                <article>
                  <h4>Safety &amp; Quality</h4>
                  <p>Strong commitment to HSE, quality management, incident-free operations and operational excellence.</p>
                </article>
                <article>
                  <h4>Marine Assets</h4>
                  <p>Access to offshore construction vessels, marine equipment and specialized operational resources.</p>
                </article>
                <article>
                  <h4>Technical Expertise</h4>
                  <p>Multidisciplinary capabilities covering engineering, construction, installation, subsea operations, commissioning and project management.</p>
                </article>
                <article>
                  <h4>Strategic Partnerships</h4>
                  <p>Strong relationships and partnerships supporting efficient project execution and technical delivery.</p>
                </article>
              </div>
            </section>

            <section className="story-detail story-location">
              <h3>Location</h3>
              <p>KM 6, East-West Road, Port Harcourt, Rivers State, Nigeria</p>
            </section>
          </div>

        </div>

      </section>


      {/* =====================================================
          WHO WE ARE
      ===================================================== */}
      <section className="who-intro">

        <div className="who-container">

          <div className="intro-grid">

            <div className="intro-content">

              <span className="who-label">
                02 — WHO WE ARE
              </span>

              <h2>
                Built to
                <br />
                <span>deliver.</span>
              </h2>

              <p className="intro-lead">
                Our fully operational fabrication yard, blasting and coating
                facility, and waterfront base support offshore construction,
                maintenance and marine logistics across the Niger Delta.
              </p>

              <p>
                With experienced personnel, marine assets and established
                operating processes, SEML delivers fabrication and project
                support with a focus on safety, quality and schedule performance.
              </p>

            </div>


            <div className="intro-image">

              <img
                src="/images/jackup barge.webp"
                alt="Energy and maritime industry"
              />

            </div>

          </div>

          <button
            type="button"
            className="who-cta-button intro-read-more"
            aria-expanded={isFacilitiesExpanded}
            aria-controls="who-we-are-details"
            onClick={() => setIsFacilitiesExpanded((expanded) => !expanded)}
          >
            {isFacilitiesExpanded ? "View less" : "View more"}
            <span aria-hidden="true">{isFacilitiesExpanded ? "−" : "+"}</span>
          </button>

          <div
            className="intro-more"
            id="who-we-are-details"
            hidden={!isFacilitiesExpanded}
          >
            <section className="intro-more-section">
              <h3>Fabrication Yard - Port Harcourt</h3>
              <p>
                Our fully operational fabrication facility in Port Harcourt
                provides dedicated support for oil &amp; gas fabrication,
                construction and maintenance projects.
              </p>
              <p>
                The facility comprises a 4-bay fabrication workshop with
                approximately 40 sqm of working space and is equipped with
                essential fabrication and mechanical equipment, including:
              </p>
              <ul>
                <li>Overhead crane</li>
                <li>Industrial lathe machines</li>
                <li>Electrical welding machines</li>
                <li>Material handling equipment</li>
                <li>Tow trucks</li>
                <li>Dedicated 250 kVA power generating set</li>
                <li>Fabrication &amp; assembly equipment</li>
              </ul>
              <p>
                With more than two decades of experience, SEML has supported
                fabrication activities for the oil &amp; gas construction
                industry, delivering reliable fabrication solutions while
                maintaining focus on quality, safety, productivity and schedule
                performance.
              </p>
              <h4>Key Capabilities</h4>
              <p>
                Steel Fabrication | Mechanical Fabrication | Welding |
                Equipment Assembly | Oil &amp; Gas Construction | Maintenance
                Support | Structural Fabrication
              </p>
            </section>

            <section className="intro-more-section">
              <h3>Blasting &amp; Coating Facility</h3>
              <p>
                SEML operates a dedicated blasting and coating facility in Port
                Harcourt supporting fabrication, maintenance and offshore
                construction activities. The facility covers approximately 25
                sqm and is designed to support both day and night operations.
              </p>
              <h4>Equipment &amp; Capabilities</h4>
              <ul>
                <li>Industrial blasting pots</li>
                <li>Pneumatic compressors</li>
                <li>Surface preparation equipment</li>
                <li>Protective coating application</li>
                <li>Fabrication surface treatment</li>
                <li>Off-site blasting &amp; coating services</li>
              </ul>
              <p>
                The facility supports surface preparation, abrasive blasting
                and protective coating activities before equipment and
                fabricated components are mobilized to project locations.
              </p>
              <h4>Key Capabilities</h4>
              <p>
                Abrasive Blasting | Surface Preparation | Protective Coating |
                Corrosion Protection | Fabrication Finishing | Equipment
                Preparation
              </p>
            </section>

            <section className="intro-more-section">
              <h3>Hercules Jetty - Delta State</h3>
              <p>
                Our 37-acre Hercules waterfront facility provides strategic
                access to the Warri waterways and supports SEML&apos;s operations
                across the offshore oil &amp; gas fields of Delta State and the
                wider Niger Delta region.
              </p>
              <p>The facility provides an important operational base for:</p>
              <ul>
                <li>Marine logistics</li>
                <li>Offshore equipment mobilization</li>
                <li>Barge operations</li>
                <li>Cargo handling</li>
                <li>Vessel &amp; marine support</li>
                <li>Project material movement</li>
                <li>Offshore construction support</li>
              </ul>
              <p>
                Its strategic waterfront location enables SEML to respond
                efficiently to the logistics and operational requirements of
                clients working across the Delta State offshore and swamp oil
                &amp; gas environment.
              </p>
            </section>

            <section className="intro-more-section">
              <h3>People, Technology &amp; Operational Excellence</h3>
              <p>
                SEML employs a skilled local workforce supported by experienced
                and highly qualified oil &amp; gas, marine, engineering and
                construction professionals.
              </p>
              <p>
                We combine local expertise, industry experience, established
                operational processes and modern technologies to deliver
                projects safely, efficiently and to the required quality
                standards.
              </p>
              <p>
                Our integrated approach to fabrication, marine logistics,
                project planning, scheduling, quality control and operational
                management enables us to maximize:
              </p>
              <p>
                Safety | Efficiency | Quality | Productivity | Cost
                Effectiveness | Schedule Performance | Customer Satisfaction
              </p>
            </section>

            <section className="intro-more-section">
              <h3>Industry Affiliations</h3>
              <p>
                As an active participant in the offshore, marine and oil &amp;
                gas industry, Strides Energy &amp; Maritime Limited supports and
                participates in professional programs, initiatives and
                activities focused on industry advancement, technical
                development, safety and operational excellence.
              </p>
              <p>
                Our industry affiliations provide access to international best
                practices, technical knowledge, professional networks and
                industry standards, strengthening our ability to deliver
                quality services to our clients.
              </p>
              <h4>Proud Members Of</h4>
              <ul>
                <li>Association of Diving Contractors International (ADCI)</li>
                <li>Oil Companies International Marine Forum (OCIMF)</li>
                <li>International Marine Contractors Association (IMCA)</li>
              </ul>
            </section>
          </div>

        </div>

      </section>


      {/* =====================================================
          VISION & MISSION
      ===================================================== */}
      <section className="vision-section">

        <div className="who-container">

          <div className="section-heading centered">

            <span className="who-label">
              03 — OUR DIRECTION
            </span>

            <h2>
              Vision & <span>Mission.</span>
            </h2>

          </div>


          <div className="vision-grid">

            <div className="vision-card">

              <span className="card-number">
                01
              </span>

              <h3>
                Our Vision
              </h3>

              <p>
                To become a
                <strong> leading asset solutions and offshore energy services
                provider in Africa</strong>, recognized for
                <strong> technical excellence, safety, innovation, reliability
                and superior project delivery</strong>.
              </p>

            </div>


            <div className="mission-card">

              <span className="card-number">
                02
              </span>

              <h3>
                Our Mission
              </h3>

              <p>
                To deliver
                <strong> integrated engineering, procurement, construction,
                installation and commissioning services</strong> that provide
                <strong> safe, efficient, reliable and cost-effective
                solutions</strong> to the energy and maritime industries.
              </p>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          CORE VALUES
      ===================================================== */}
      <section className="values-section">

        <div className="who-container">

          <div className="section-heading">

            <span className="who-label">
              04 — OUR CORE VALUES
            </span>

            <h2>
              What drives
              <span> us.</span>
            </h2>

          </div>


          <div className="values-grid">

            <div className="value-card">

              <span>01</span>

              <h3>
                Safety First
              </h3>

              <p>
                We put the safety of our people,
                clients and environment at the heart
                of everything we do.
              </p>

            </div>


            <div className="value-card">

              <span>02</span>

              <h3>
                Integrity
              </h3>

              <p>
                We conduct our business with honesty,
                transparency and accountability.
              </p>

            </div>


            <div className="value-card">

              <span>03</span>

              <h3>
                Excellence
              </h3>

              <p>
                We strive for high standards in every
                project, service and interaction.
              </p>

            </div>


            <div className="value-card">

              <span>04</span>

              <h3>
                Innovation
              </h3>

              <p>
                We embrace better ideas, technology
                and new ways of solving problems.
              </p>

            </div>


            <div className="value-card">

              <span>05</span>

              <h3>
                Partnership
              </h3>

              <p>
                We build strong relationships based
                on trust, collaboration and shared success.
              </p>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          HSE COMMITMENT
      ===================================================== */}
      <section className="hse-section">

        <div className="who-container">

          <div className="hse-grid">

            <div className="hse-image">

              <img
                src="/images/community.webp"
                alt="Safety and community commitment"
              />

            </div>


            <div className="hse-content">

              <span className="who-label">
                05 — HSE COMMITMENT
              </span>

              <h2>
                Safety is not
                <br />
                <span>optional.</span>
              </h2>

              <p>
                Health, Safety and Environmental responsibility
                is fundamental to how we operate.
              </p>

              <p>
                We are committed to creating safe working
                environments, protecting our people and
                minimising the environmental impact of our
                operations.
              </p>


              <div className="hse-points">

                <div>
                  <strong>01</strong>
                  <span>Health & Safety</span>
                </div>

                <div>
                  <strong>02</strong>
                  <span>Environmental Responsibility</span>
                </div>

                <div>
                  <strong>03</strong>
                  <span>Operational Discipline</span>
                </div>

              </div>

            </div>

          </div>

        </div>

      </section>


      {/* =====================================================
          CTA
      ===================================================== */}
      <section className="who-cta">

        <div className="who-container">

          <span className="who-label">
            LET'S WORK TOGETHER
          </span>

          <h2>
            Ready to build
            <br />
            <span>what's next?</span>
          </h2>

          <p>
            Let's create solutions that move your
            operations forward.
          </p>

          <a
            href="/contact"
            className="who-cta-button"
          >
            Get In Touch
          </a>

        </div>

      </section>

      {isCeoVideoOpen && (
        <div
          className="who-video-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setIsCeoVideoOpen(false);
            }
          }}
        >
          <section
            ref={videoDialogRef}
            className="who-video-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="who-video-title"
          >
            <button
              ref={videoCloseButtonRef}
              type="button"
              className="who-video-close"
              aria-label="Close CEO video"
              onClick={() => setIsCeoVideoOpen(false)}
            >
              ×
            </button>
            <h2 id="who-video-title">A few words from our CEO</h2>
            <video
              className="who-video-player"
              controls
              preload="none"
              playsInline
              aria-label="CEO message"
            >
              <source src="/videos/SEML - About Us.mp4" type="video/mp4" />
              Your browser does not support the video element.
            </video>
            <p className="who-video-fallback">
              Video not playing? <a href="/videos/SEML - About Us.mp4">Open the video</a>.
            </p>
          </section>
        </div>
      )}

    </main>
  );
}

export default WhoWeAre;